import useProvidersForClient from '@gr/portal/hooks/useProvidersForClient';
import { CampaignTypeEnum } from '@gr/shared/enums';
import { useEffect, useMemo } from 'react';
import { Dropdown, IDropdownValue } from '../Dropdown';

interface IProviderForClientDropdownProps {
  value: any;
  clientId: string;
  campaignType: CampaignTypeEnum;
  onChange: (newValue: IDropdownValue | undefined) => void;
  onBlur?: { (e: any): void; <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void; };
  disabled?: boolean;
  touched?: boolean;
  errorMessage?: string;
}

const ProviderForClientDropdown = ({
  value,
  clientId,
  campaignType,
  errorMessage,
  disabled,
  touched,
  onChange,
  onBlur,
}: IProviderForClientDropdownProps): JSX.Element => {
  const [{ data, loading, error }, fetchProviders] = useProvidersForClient(clientId, campaignType);

  // Memoized so the option objects keep a stable identity across renders. Headless UI's Listbox
  // compares the selected value against the options by reference, so rebuilding these every render
  // invalidates its internal data context on every parent render and re-pushes it to every option.
  const dropdownValues: IDropdownValue[] = useMemo(
    () =>
      data?.data?.providers.map((pt) => ({
        label: pt.name,
        value: pt.id,
      })) ?? [],
    [data]
  );

  // Resolved from the memoized list for the same reason. `null` rather than `undefined` keeps the
  // Listbox controlled from the first render, so it never flips uncontrolled -> controlled when the
  // provider map resolves after the provider list.
  const selectedOption = useMemo(
    () => (value ? dropdownValues.find((ct) => ct.value === value) ?? null : null),
    [dropdownValues, value]
  );

  useEffect(() => {
    if (clientId) {
      fetchProviders({ data: { clientId: clientId, campaignType: campaignType } });
    }
  }, [clientId, campaignType]);

  return (
    <Dropdown
      full
      value={selectedOption}
      options={dropdownValues}
      loading={loading}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled || dropdownValues?.length === 0 || !clientId}
      placeholder="Select a Provider"
      error={touched && dropdownValues?.length === 0 && clientId ? 'No Providers for Client' : errorMessage}
    />
  );
};

export default ProviderForClientDropdown;
